import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip } from 'chart.js';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatMoney, monthNames } from '../../utils/formatters';
import TransactionModal from '../Transactions/TransactionModal';
import { PayDebtModal } from '../Debts/Debts';
import { PayBillModal } from '../Accounts/Accounts';
import { ConfirmProventoModal } from '../Investments/Investments';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const round2 = v => Math.round(v * 100) / 100;

export default function CalendarView() {
  const { state } = useApp();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  const [payingDebt, setPayingDebt] = useState(null);
  const [payingCard, setPayingCard] = useState(null);
  const [expandedBill, setExpandedBill] = useState(null);
  const [confirmingProvento, setConfirmingProvento] = useState(null);
  const activeAccounts = useMemo(() => state.accounts.filter(a => a.active), [state.accounts]);

  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  const txByDay = useMemo(() => {
    const map = {};
    state.transactions.filter(t => t.date.startsWith(monthKey)).forEach(t => {
      const d = parseInt(t.date.split('-')[2]);
      if (!map[d]) map[d] = [];
      map[d].push(t);
    });
    // Inject active debt installments on their due day
    const monthStart = new Date(year, month, 1);
    const monthEnd   = new Date(year, month + 1, 0);
    (state.debts || []).filter(debt => !debt.settled && debt.installment > 0 && debt.dueDay).forEach(debt => {
      const start = debt.startDate ? new Date(debt.startDate + 'T00:00:00') : null;
      const end   = debt.endDate   ? new Date(debt.endDate   + 'T00:00:00') : null;
      const dueInMonth = new Date(year, month, debt.dueDay);
      if (dueInMonth < monthStart || dueInMonth > monthEnd) return;
      if (start && dueInMonth < start) return;
      if (end   && dueInMonth > end)   return;
      const d = debt.dueDay;
      if (!map[d]) map[d] = [];
      map[d].push({ id: `debt-${debt.id}`, type: 'expense', status: 'pending', value: debt.installment, description: `Parcela: ${debt.name}`, categoryId: null, _isDebt: true, _debtRef: debt });
    });

    // Injeta proventos previstos (dividendo/JCP anunciado pela corretora, ainda
    // não pago) nas datas certas — importados via PDF em Investimentos.
    (state.investments || []).forEach(inv => {
      (inv.proventosPrevistos || []).forEach(prov => {
        if (!prov.data || !prov.data.startsWith(monthKey)) return;
        const d = parseInt(prov.data.split('-')[2]);
        if (!map[d]) map[d] = [];
        map[d].push({
          id: `provento-${inv.id}-${prov.id}`,
          type: 'income', status: 'pending', value: prov.valorLiquido,
          description: `Provento previsto: ${inv.name}`, categoryId: 'inv',
          _isProvento: true, _investment: inv, _provento: prov,
        });
      });
    });

    // Agrupa as compras de cada cartão (mesmo dia + mesmo cartão) numa única
    // entrada "Fatura" — sem isso, uma fatura com 10+ compras poluía o dia
    // inteiro com uma linha por compra. Todas as compras de um cartão já caem
    // no mesmo dia (data de vencimento do cartão, definida ao lançar a compra).
    Object.keys(map).forEach(d => {
      const dayTxs = map[d];
      const others = dayTxs.filter(t => !t.cardId);
      const byCard = {};
      dayTxs.filter(t => t.cardId).forEach(t => {
        (byCard[t.cardId] = byCard[t.cardId] || []).push(t);
      });
      const groups = Object.entries(byCard).map(([cardId, items]) => {
        const card = state.cards.find(c => c.id === cardId);
        const total = round2(items.reduce((s, t) => s + t.value, 0));
        const allPaid = items.every(t => t.status === 'paid');
        return {
          id: `cardbill-${cardId}-${d}`,
          type: 'expense',
          status: allPaid ? 'paid' : 'pending',
          value: total,
          description: `Fatura ${card?.name || 'Cartão'}`,
          categoryId: null,
          _isCardBill: true,
          _card: card,
          _items: items,
        };
      });
      map[d] = [...others, ...groups];
    });

    return map;
  }, [state.transactions, state.debts, state.cards, state.investments, monthKey, year, month]);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const projData = useMemo(() => {
    const currentBalance = state.accounts.filter(a => a.active).reduce((s, a) => s + a.balance, 0);
    // Remove this month's paid transactions from the starting balance to avoid double-counting
    // (account balance already includes them; projData will re-add them day by day).
    // Faturas de cartão (_isCardBill) não mexem no saldo por si só — o pagamento
    // de verdade vira uma transação própria ("Pagamento de fatura") quando a
    // fatura é paga, e essa sim entra na conta normalmente.
    const monthPaidDelta = Object.values(txByDay).flat()
      .filter(t => t.status === 'paid' && !t._isCardBill)
      .reduce((s, t) => s + (t.type === 'income' ? t.value : -t.value), 0);
    let running = currentBalance - monthPaidDelta;
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = i + 1;
      (txByDay[d] || []).forEach(t => {
        if (t.status === 'paid' && !t._isCardBill) running += t.type === 'income' ? t.value : -t.value;
      });
      return { d, balance: running };
    });
  }, [txByDay, daysInMonth, state.accounts]);

  const selectedTxs = selectedDay ? (txByDay[selectedDay] || []) : [];

  // Monthly summary — soma TOTAL (pago + pendente), pra dar uma ideia real do que
  // entra/sai no mês inteiro, não só o que já foi de fato pago até agora.
  const monthTxs = Object.values(txByDay).flat().filter(t => !t.cardId && !t._isCardBill);
  const monthIncome = monthTxs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const monthExpense = monthTxs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);
  const monthIncomePaid = monthTxs.filter(t => t.type === 'income' && t.status === 'paid').reduce((s, t) => s + t.value, 0);
  const monthExpensePaid = monthTxs.filter(t => t.type === 'expense' && t.status === 'paid').reduce((s, t) => s + t.value, 0);

  // Build grid cells: empty slots + day slots
  const totalCells = firstDay + daysInMonth;
  const rows = Math.ceil(totalCells / 7);

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="cal-page-header">
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Calendário</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Visualize seus lançamentos no tempo</div>
        </div>
        <div className="cal-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={prev} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--card-bg)'}
          >
            <i className="fas fa-chevron-left" style={{ fontSize: 12 }}></i>
          </button>
          <span style={{ fontSize: 15, fontWeight: 700, minWidth: 130, textAlign: 'center', color: 'var(--text)' }}>
            {monthNames[month]} {year}
          </span>
          <button onClick={next} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--card-bg)'}
          >
            <i className="fas fa-chevron-right" style={{ fontSize: 12 }}></i>
          </button>
        </div>
      </div>

      {/* Monthly summary strip — scroll horizontal no mobile */}
      <div className="cal-summary-strip">
        <div className="card cal-summary-card">
          <div className="metric-label">Receitas do mês</div>
          <div className="cal-summary-value" style={{ color: 'var(--green)' }}>+{formatCurrency(monthIncome)}</div>
          <div className="cal-summary-sub">
            <i className="fas fa-check" style={{ marginRight: 4 }}></i>{formatCurrency(monthIncomePaid)} já recebido
          </div>
        </div>
        <div className="card cal-summary-card">
          <div className="metric-label">Despesas do mês</div>
          <div className="cal-summary-value" style={{ color: 'var(--red)' }}>
            {monthExpense > 0 ? '-' : ''}{formatCurrency(monthExpense)}
          </div>
          <div className="cal-summary-sub">
            <i className="fas fa-check" style={{ marginRight: 4 }}></i>{formatCurrency(monthExpensePaid)} já pago
          </div>
        </div>
        <div className="card cal-summary-card">
          <div className="metric-label">Saldo do mês</div>
          <div className="cal-summary-value" style={{ color: monthIncome - monthExpense >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {monthIncome - monthExpense >= 0 ? '+' : ''}{formatCurrency(monthIncome - monthExpense)}
          </div>
          <div className="cal-summary-sub">
            Realizado: {monthIncomePaid - monthExpensePaid >= 0 ? '+' : ''}{formatCurrency(monthIncomePaid - monthExpensePaid)}
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
        {/* Weekday header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '2px solid var(--border)' }}>
          {WEEKDAYS.map((d, i) => (
            <div key={d} style={{
              padding: '10px 0',
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: i === 0 ? '#e24b4a' : i === 6 ? '#378ADD' : 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              background: 'var(--surface2)',
            }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {Array.from({ length: rows * 7 }).map((_, idx) => {
            const dayNum = idx - firstDay + 1;
            const isValid = dayNum >= 1 && dayNum <= daysInMonth;
            const isToday = isValid && year === now.getFullYear() && month === now.getMonth() && dayNum === now.getDate();
            const isSelected = isValid && selectedDay === dayNum;
            const dayTxs = isValid ? (txByDay[dayNum] || []) : [];
            const hasIncome = dayTxs.some(t => t.type === 'income');
            const hasExpense = dayTxs.some(t => t.type === 'expense');
            const isWeekend = idx % 7 === 0 || idx % 7 === 6;
            const col = idx % 7;
            const row = Math.floor(idx / 7);
            const totalRows = rows;
            const isLastRow = row === totalRows - 1;
            const isLastCol = col === 6;

            return (
              <div
                key={idx}
                onClick={() => isValid && setSelectedDay(selectedDay === dayNum ? null : dayNum)}
                className="cal-day-cell"
                style={{
                  borderRight: isLastCol ? 'none' : '1px solid var(--border)',
                  borderBottom: isLastRow ? 'none' : '1px solid var(--border)',
                  background: isSelected
                    ? 'rgba(13,27,42,0.06)'
                    : isToday
                    ? 'rgba(201,162,39,0.07)'
                    : isWeekend && isValid
                    ? 'rgba(0,0,0,0.015)'
                    : 'var(--card-bg)',
                  cursor: isValid ? 'pointer' : 'default',
                  transition: 'background 0.12s',
                  position: 'relative',
                }}
                onMouseEnter={e => { if (isValid && !isSelected) e.currentTarget.style.background = 'var(--surface2)'; }}
                onMouseLeave={e => {
                  if (isValid && !isSelected) e.currentTarget.style.background =
                    isToday ? 'rgba(201,162,39,0.07)' : isWeekend ? 'rgba(0,0,0,0.015)' : 'var(--card-bg)';
                }}
              >
                {isValid && (
                  <>
                    {/* Day number */}
                    <div style={{
                      width: 26, height: 26,
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: isToday ? 800 : 500,
                      color: isToday ? '#fff' : isWeekend ? (col === 0 ? '#e24b4a' : '#378ADD') : 'var(--text)',
                      background: isToday ? 'var(--navy)' : 'transparent',
                      marginBottom: 4,
                    }}>
                      {dayNum}
                    </div>

                    {/* Transaction previews */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {dayTxs.slice(0, 2).map(tx => (
                        <div
                          key={tx.id}
                          onClick={e => { e.stopPropagation(); setSelectedDay(dayNum); if (!tx._isDebt && !tx._isCardBill && !tx._isProvento) setEditingTx(tx); }}
                          title={tx.description}
                          className="cal-tx-chip"
                          style={{
                            color: tx.type === 'income' ? '#0F6E56' : '#991b1b',
                            background: tx.type === 'income' ? '#d1fae5' : '#fee2e2',
                            cursor: tx._isDebt || tx._isCardBill || tx._isProvento ? 'default' : 'pointer',
                          }}
                        >
                          {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                        </div>
                      ))}
                      {dayTxs.length > 2 && (
                        <div style={{ fontSize: 10, color: 'var(--muted)', paddingLeft: 4 }}>
                          +{dayTxs.length - 2} mais
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: '#d1fae5', border: '1px solid #6ee7b7', display: 'inline-block' }}></span>Receita
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: '#fee2e2', border: '1px solid #fca5a5', display: 'inline-block' }}></span>Despesa
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--navy)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 800 }}>H</span>Hoje
        </span>
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="section-title" style={{ margin: 0 }}>
              <i className="fas fa-calendar-day" style={{ marginRight: 8, color: 'var(--gold)' }}></i>
              {selectedDay} de {monthNames[month]} de {year}
            </div>
            <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          {selectedTxs.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0', textAlign: 'center' }}>
              <i className="fas fa-inbox" style={{ fontSize: 20, opacity: 0.4, display: 'block', marginBottom: 6 }}></i>
              Nenhuma transação neste dia
            </div>
          ) : selectedTxs.map(tx => {
            const cat = cats[tx.categoryId];
            const isDebt = tx._isDebt;
            const isCardBill = tx._isCardBill;
            const isProvento = tx._isProvento;
            const isExpanded = isCardBill && expandedBill === tx.id;
            const clickable = !isDebt && !isCardBill && !isProvento;
            return (
              <div key={tx.id}>
                <div
                  className="tx-item"
                  onClick={() => {
                    if (clickable) setEditingTx(tx);
                    else if (isCardBill) setExpandedBill(isExpanded ? null : tx.id);
                  }}
                  style={{ cursor: clickable || isCardBill ? 'pointer' : 'default', borderRadius: 8, transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (clickable || isCardBill) e.currentTarget.style.background = 'var(--surface2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div className="tx-info">
                    <div className="tx-desc">
                      {isCardBill && <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: 9, marginRight: 7, color: 'var(--muted)' }}></i>}
                      {tx.description}
                    </div>
                    <div className="tx-cat">
                      {isCardBill ? `${tx._items.length} compra${tx._items.length !== 1 ? 's' : ''} · fecha dia ${tx._card?.closeDay ?? '?'}` : cat?.name}
                      {clickable && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--muted)' }}><i className="fas fa-pen"></i></span>}
                    </div>
                  </div>
                  <div className="tx-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div className={`tx-amount ${tx.type === 'income' ? 'text-green' : 'text-red'}`}>
                      {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                    </div>
                    {isDebt ? (
                      <button
                        onClick={e => { e.stopPropagation(); setPayingDebt(tx._debtRef); }}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #1d9e75', background: 'rgba(29,158,117,0.08)', cursor: 'pointer', fontSize: 11, color: '#1d9e75', fontWeight: 600 }}
                      >
                        <i className="fas fa-money-bill-wave" style={{ marginRight: 4 }}></i>Baixar
                      </button>
                    ) : isCardBill && tx.status !== 'paid' ? (
                      <button
                        onClick={e => { e.stopPropagation(); setPayingCard(tx._card); }}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #1d9e75', background: 'rgba(29,158,117,0.08)', cursor: 'pointer', fontSize: 11, color: '#1d9e75', fontWeight: 600 }}
                      >
                        <i className="fas fa-money-bill-wave" style={{ marginRight: 4 }}></i>Pagar fatura
                      </button>
                    ) : isProvento ? (
                      <button
                        onClick={e => { e.stopPropagation(); setConfirmingProvento({ investment: tx._investment, provento: tx._provento }); }}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #378ADD', background: 'rgba(55,138,221,0.08)', cursor: 'pointer', fontSize: 11, color: '#378ADD', fontWeight: 600 }}
                      >
                        <i className="fas fa-calendar-check" style={{ marginRight: 4 }}></i>Confirmar
                      </button>
                    ) : (
                      <span className={`tag ${tx.status === 'paid' ? 'tag-green' : 'tag-amber'}`} style={{ fontSize: 10 }}>
                        {tx.status === 'paid' ? 'Pago' : 'Pendente'}
                      </span>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ paddingLeft: 24, marginBottom: 6 }}>
                    {tx._items.map(item => {
                      const itemCat = cats[item.categoryId];
                      return (
                        <div
                          key={item.id}
                          className="tx-item"
                          onClick={e => { e.stopPropagation(); setEditingTx(item); }}
                          style={{ cursor: 'pointer', borderRadius: 8, transition: 'background 0.12s', padding: '8px 12px' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div className="tx-info">
                            <div className="tx-desc" style={{ fontSize: 12.5 }}>{item.description}</div>
                            <div className="tx-cat" style={{ fontSize: 11 }}>{itemCat?.name}</div>
                          </div>
                          <div className="tx-right">
                            <div className="tx-amount text-red" style={{ fontSize: 12.5 }}>-{formatCurrency(item.value)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingTx && (
        <TransactionModal tx={editingTx} onClose={() => setEditingTx(null)} />
      )}
      {payingDebt && (
        <PayDebtModal debt={payingDebt} accounts={activeAccounts} onClose={() => setPayingDebt(null)} />
      )}
      {payingCard && (
        <PayBillModal card={payingCard} onClose={() => setPayingCard(null)} />
      )}
      {confirmingProvento && (
        <ConfirmProventoModal
          investment={confirmingProvento.investment}
          provento={confirmingProvento.provento}
          onClose={() => setConfirmingProvento(null)}
        />
      )}

      {/* Saldo projetado */}
      <div className="card">
        <div className="section-title">
          <i className="fas fa-chart-line" style={{ marginRight: 8, color: 'var(--gold)' }}></i>
          Saldo projetado — {monthNames[month]}
        </div>
        <div style={{ height: 130 }}>
          <Line
            data={{
              labels: projData.map(p => p.d),
              datasets: [{
                label: 'Saldo',
                data: projData.map(p => p.balance),
                borderColor: '#c9a227',
                backgroundColor: 'rgba(201,162,39,0.08)',
                fill: true,
                tension: 0.4,
                pointRadius: 2,
                pointHoverRadius: 5,
                borderWidth: 2,
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 10 } },
                y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 }, callback: v => 'R$' + Math.round(v / 1000) + 'k' } }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
